import React, { useRef, useEffect, useState, useCallback } from 'react';
import './ServicesSection.css';

const services = [
  { icon: '✦', title: 'Planung & Gestaltung',    description: 'Von der ersten Skizze bis zur fertigen Anlage — maßgeschneiderte Lösungen für Ihren Außenbereich.' },
  { icon: '◈', title: 'Pflege & Instandhaltung', description: 'Professionelle Pflege und regelmäßige Instandhaltung für Ihre Grünflächen und Anlagen.' },
  { icon: '⬡', title: 'Pflasterarbeiten',         description: 'Einfahrten, Terrassen, Wege — wir pflastern sauber, dauerhaft und nach Ihren Wünschen.' },
  { icon: '◭', title: 'Tiefbauarbeiten',           description: 'Alle Arten von Erdbewegungen — zuverlässig, mit dem richtigen Gerät und voller Professionalität.' },
  { icon: '❋', title: 'Rollrasenverlegung',        description: 'Wir verlegen Rollrasenflächen in jeder Größe und Form — sofort grün, sofort schön.' },
  { icon: '⌁', title: 'Bewässerung',               description: 'Automatisierte Systeme, die Wasser sparen und Ihre Pflanzen das ganze Jahr gesund halten.' },
  { icon: '⟁', title: 'Baumarbeiten',              description: 'Professioneller Baumschnitt und Fällarbeiten für gesunde, sichere Gärten.' },
  { icon: '⊞', title: 'Aufbau & Montage',          description: 'Von Holzzäunen bis Pergolen — wir montieren alles handwerklich sauber und langlebig.' },
  { icon: '◉', title: 'Beleuchtung & Elektrik',    description: 'Funktionale und ästhetische Gartenbeleuchtung sowie Elektroarbeiten für den Außenbereich.' },
  { icon: '✿', title: 'Kundenbetreuung',           description: 'Unsere Experten sind für Sie da — von der ersten Frage bis zur finalen Abnahme.' }
];

// ── Scroll reveal hook ──────────────────────────────────
function useReveal(containerRef) {
  useEffect(() => {
    if (!containerRef.current) return;
    const els = containerRef.current.querySelectorAll('.srv-reveal');
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('srv-visible'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [containerRef]);
}

// ── Mobile: Planmappe ───────────────────────────────────
// Goldtusche-Skizzen je Leistung (viewBox 120 × 80), gleiche Reihenfolge wie `services`
const ART = [
  // Planung & Gestaltung – Blatt, Winkel, Zirkel
  ['M14 66V14H78V66Z', 'M22 58V30L50 58Z', 'M28 22H70', 'M14 73H78M14 70V76M78 70V76', 'M100 14L88 66M100 14L112 66M91 50H109', 'M100 14m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0'],
  // Pflege – Schere und Blatt
  ['M22 60L70 32', 'M22 32L70 60', 'M22 60m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M22 32m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M84 46C92 28 106 24 112 28C110 44 98 52 84 46Z', 'M84 46L110 29'],
  // Pflasterarbeiten – Fläche in Perspektive
  ['M8 72L38 12H82L112 72Z', 'M30 72L49 12', 'M60 72V12', 'M90 72L71 12', 'M31 26H89', 'M23 42H97', 'M15 57H105'],
  // Tiefbauarbeiten – Schaufel über Bodenschichten
  ['M6 56C30 51 50 60 72 55S104 52 114 56', 'M6 66C34 63 58 69 86 64S108 64 114 66', 'M6 74H114', 'M28 8L62 20L82 40', 'M82 40L66 50L58 34Z', 'M66 50l1 6M71 47l1 6M76 44l1 6'],
  // Rollrasenverlegung – Rolle und ausgerollte Bahn
  ['M30 50m-16 0a16 16 0 1 0 32 0a16 16 0 1 0-32 0', 'M30 50m-7 0a7 7 0 1 0 14 0a7 7 0 1 0-14 0', 'M30 66H112V34H30', 'M52 34l2-7M62 34l-1-7M72 34l2-7M82 34l-1-7M92 34l2-7M102 34l-1-7'],
  // Bewässerung – Regner mit Wasserbögen
  ['M60 72V52', 'M50 72H70', 'M54 52H66V46H54Z', 'M60 46C48 30 30 24 12 28', 'M60 46C72 30 90 24 108 28', 'M60 46C52 36 40 34 30 38', 'M60 46C68 36 80 34 90 38', 'M22 44c-3 4-3 7 0 7s3-3 0-7ZM98 44c-3 4-3 7 0 7s3-3 0-7Z'],
  // Baumarbeiten – Baum mit Schnittlinie
  ['M60 74V50', 'M60 62L52 56M60 58L68 52', 'M60 6C42 6 32 18 36 30C24 34 26 50 40 50H80C94 50 96 34 84 30C88 18 78 6 60 6Z', 'M8 74H112', 'M96 28l12 -6M98 34l12 -2'],
  // Aufbau & Montage – Pergola
  ['M22 74V24M98 74V24', 'M12 24H108', 'M12 18H108', 'M30 10V28M50 10V28M70 10V28M90 10V28', 'M22 60H98M22 50H98', 'M30 50V60M40 50V60M50 50V60M60 50V60M70 50V60M80 50V60M90 50V60'],
  // Beleuchtung & Elektrik – Gartenleuchte
  ['M52 74V36H68V74', 'M48 36H72L66 26H54Z', 'M60 20V8M44 28L35 20M76 28L85 20M40 42H28M80 42H92', 'M18 74H102'],
  // Kundenbetreuung – Gespräch
  ['M12 14H70V46H36L24 58V46H12Z', 'M24 26H58M24 35H48', 'M50 34H108V62H98V72L86 62H50Z', 'M60 44H98M60 52H86'],
];

const pad = (n) => String(n).padStart(2, '0');

function MobileDeck() {
  const total = services.length;
  const [active, setActive] = useState(0);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef(null);
  const indexRef = useRef(null);

  const go = useCallback((dir) => {
    setActive((a) => (a + dir + total) % total);
  }, [total]);

  // Register mitführen, ohne die Seite zu scrollen
  useEffect(() => {
    const box = indexRef.current;
    const chip = box && box.children[active];
    if (!chip) return;
    box.scrollTo({ left: chip.offsetLeft - (box.clientWidth - chip.offsetWidth) / 2, behavior: 'smooth' });
  }, [active]);

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('a, button')) return;
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, locked: false };
  };
  const onPointerMove = (e) => {
    const s = start.current;
    if (!s || s.id !== e.pointerId) return;
    const mx = e.clientX - s.x;
    if (!s.locked) {
      if (Math.abs(mx) < 8) return;
      if (Math.abs(e.clientY - s.y) > Math.abs(mx)) { start.current = null; return; }
      s.locked = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setDragging(true);
    }
    setDx(mx);
  };
  const onPointerEnd = () => {
    const s = start.current;
    start.current = null;
    if (!s || !s.locked) return;
    setDragging(false);
    if (dx < -70) go(1);
    else if (dx > 70) go(-1);
    setDx(0);
  };

  const pull = Math.min(1, Math.abs(dx) / 140);

  return (
    <div className="srv-mobile">
      <div className="srv-index" ref={indexRef} role="tablist" aria-label="Leistungen">
        {services.map((svc, i) => (
          <button
            key={svc.title}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`srv-index-chip${i === active ? ' is-on' : ''}`}
            onClick={() => setActive(i)}
          >
            <em>{pad(i + 1)}</em>{svc.title}
          </button>
        ))}
      </div>

      <div
        className={`srv-deck${dragging ? ' is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        aria-live="polite"
      >
        {services.map((svc, i) => {
          const d = (i - active + total) % total;   // 0 = oben, total-1 = links abgelegt
          const depth = d === total - 1 ? 'out' : Math.min(d, 3);
          let style;
          if (d === 0 && dx) style = { transform: `translateX(${dx}px) rotate(${dx * 0.045}deg)` };
          else if ((d === 1 || d === 2) && pull) style = { '--pull': dx < 0 ? pull : 0 };
          else if (d === total - 1 && dx > 0) style = { transform: `translateX(calc(-125% + ${dx * 1.4}px)) rotate(${-9 + pull * 9}deg)` };
          return (
            <article
              key={svc.title}
              className={`srv-sheet is-d${depth}`}
              style={style}
              aria-hidden={d !== 0}
            >
              <div className="srv-sheet-art">
                <span className="srv-sheet-num" aria-hidden="true">{pad(i + 1)}</span>
                <svg viewBox="0 0 120 80" aria-hidden="true" focusable="false">
                  {ART[i].map((p, k) => (
                    <path key={k} d={p} pathLength="1" style={{ animationDelay: `${120 + k * 140}ms` }} />
                  ))}
                </svg>
                <i className="srv-crop srv-crop--tl" /><i className="srv-crop srv-crop--tr" />
                <i className="srv-crop srv-crop--bl" /><i className="srv-crop srv-crop--br" />
              </div>
              <div className="srv-sheet-body">
                <span className="srv-sheet-eyebrow">Leistung {pad(i + 1)}</span>
                <h3 className="srv-sheet-title">{svc.title}</h3>
                <p className="srv-sheet-desc">{svc.description}</p>
              </div>
              <footer className="srv-sheet-foot">
                <span>Blatt <strong>{pad(i + 1)}</strong> / {pad(total)}</span>
                <a href="#contact" tabIndex={d === 0 ? 0 : -1}>Anfragen <span aria-hidden="true">→</span></a>
              </footer>
            </article>
          );
        })}
      </div>

      <div className="srv-deck-controls">
        <button type="button" className="srv-deck-btn" onClick={() => go(-1)} aria-label="Vorherige Leistung">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <div className="srv-deck-progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${(active + 1) / total})` }} />
        </div>
        <span className="srv-deck-count" aria-hidden="true"><strong>{pad(active + 1)}</strong> / {pad(total)}</span>
        <button type="button" className="srv-deck-btn" onClick={() => go(1)} aria-label="Nächste Leistung">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
      <p className="srv-deck-hint" aria-hidden="true">Wischen zum Blättern</p>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────
export default function ServicesSection() {
  const rootRef = useRef(null);
  useReveal(rootRef);

  return (
    <section id="dienstleistungen" className="srv-root" ref={rootRef}>

      {/* Header */}
      <div className="srv-header">
        <span className="srv-eyebrow srv-reveal">Leistungen</span>
        <h2 className="srv-title srv-reveal">
          Was wir für<br /><em>Sie tun</em>
        </h2>
        <p className="srv-subtitle srv-reveal">
          Von der Planung bis zur Pflege — alles aus einer Hand.
        </p>
      </div>

      {/* Desktop Bento Grid */}
      <div className="srv-grid">
        {services.map((svc, i) => (
          <div
            key={i}
            className="srv-card srv-reveal"
            style={{ transitionDelay: `${Math.min(i * 60, 480)}ms` }}
          >
            <div className="srv-card-inner">
              <span className="srv-icon" aria-hidden>{svc.icon}</span>
              <h3 className="srv-card-title">{svc.title}</h3>
              <p className="srv-card-desc">{svc.description}</p>
              <span className="srv-card-num" aria-hidden>
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>
            <div className="srv-card-glow" aria-hidden />
          </div>
        ))}
      </div>

      {/* Mobile Carousel */}
      <MobileDeck />

    </section>
  );
}